// components/CubeConfigurator.jsx
import React, { useState, useRef, useEffect } from "react";
import {
    TextField,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Stack,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UpdateIcon from "@mui/icons-material/Update";
import apiClient from "../Helpers/apiClient";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const CubeConfigurator = () => {
    const [dimensions, setDimensions] = useState({
        height: 5,
        length: 5,
        width: 5,
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [isUpdated, setIsUpdated] = useState(false);
    const [mesh, setMesh] = useState(null);

    // Updated steps to reflect the new API workflow
    const steps = [
        "Enter Dimensions",
        "Update Variables",
        "Preview 3D Model",
        "Create Drawing",
        "Download PDF",
    ];

    // Three.js refs
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);

    // Initialize Three.js scene
    useEffect(() => {
        if (!mountRef.current) return;

        // Scene setup
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);
        sceneRef.current = scene;

        // Camera
        const camera = new THREE.PerspectiveCamera(
            75,
            mountRef.current.clientWidth / mountRef.current.clientHeight,
            0.1,
            1000
        );
        camera.position.set(10, 10, 10);
        cameraRef.current = camera;

        // Renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        mountRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Lights
        const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
        scene.add(ambientLight);
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(10, 10, 5);
        directionalLight.castShadow = true;
        scene.add(directionalLight);

        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controlsRef.current = controls;

        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, cameraRef.current);
        };
        animate();

        // Cleanup
        return () => {
            if (mountRef.current && renderer.domElement) {
                mountRef.current.removeChild(renderer.domElement);
            }
            if (mesh) {
                sceneRef.current?.remove(mesh);
            }
        };
    }, []);

    // Load initial 3D model on component mount
    useEffect(() => {
        const loadInitialModel = async () => {
            setLoading(true);
            setMessage({ type: "info", text: "Loading initial 3D model..." });

            try {
                // Update variables with default dimensions
                await apiClient.post("/api/onshape/update-variables", dimensions);

                // Download and load STL
                const response = await apiClient.get("/api/onshape/download-stl", {
                    responseType: "blob",
                });

                const stlBlob = response.data;
                await loadSTL(stlBlob);

                setIsUpdated(true);
                setMessage({
                    type: "success",
                    text: "Initial 3D model loaded! Adjust dimensions and update for changes.",
                });
            } catch (error) {
                console.error("Initial load error:", error);
                setMessage({
                    type: "error",
                    text: error.response?.data?.message || "Failed to load initial 3D preview",
                });
            } finally {
                setLoading(false);
            }
        };

        loadInitialModel();
    }, []); // Empty dependency array: runs once on mount

    // Resize handler
    useEffect(() => {
        const handleResize = () => {
            if (!mountRef.current || !rendererRef.current || !cameraRef.current) return;
            cameraRef.current.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleChange = (field) => (event) => {
        setDimensions({
            ...dimensions,
            [field]: parseFloat(event.target.value) || 0,
        });
        setIsUpdated(false); // Reset update status when dimensions change
    };

    const clearSceneMeshes = () => {
        if (!sceneRef.current) return;

        // Remove all meshes from scene (keep lights, camera not added to scene)
        const meshesToRemove = sceneRef.current.children.filter((child) => child.type === "Mesh");
        meshesToRemove.forEach((mesh) => {
            sceneRef.current.remove(mesh);
            if (mesh.geometry) {
                mesh.geometry.dispose();
            }
            if (mesh.material) {
                if (Array.isArray(mesh.material)) {
                    mesh.material.forEach((mat) => mat.dispose());
                } else {
                    mesh.material.dispose();
                }
            }
        });

        // Reset mesh state
        setMesh(null);
        console.log("Cleared all meshes from scene");
    };

    const loadSTL = async (stlBlob) => {
        if (!stlBlob || !sceneRef.current || !rendererRef.current || !cameraRef.current) return;

        // Clear any remaining meshes before loading new (safety net)
        clearSceneMeshes();

        const loader = new STLLoader();
        const buffer = await stlBlob.arrayBuffer();
        const geometry = loader.parse(buffer);

        // Material
        const material = new THREE.MeshPhongMaterial({ color: 0x2196f3, shininess: 100 });

        // Mesh
        const newMesh = new THREE.Mesh(geometry, material);
        newMesh.castShadow = true;
        newMesh.receiveShadow = true;

        // Rotate to correct orientation (common fix for STL Y-up from Onshape)
        newMesh.rotation.x = -Math.PI / 2; // Rotate 90 degrees to align height (Y) upward if needed

        // Center and scale
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const center = new THREE.Vector3();
        box.getCenter(center);
        newMesh.position.sub(center);

        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        newMesh.scale.setScalar(5 / maxDim); // Scale to fit view

        sceneRef.current.add(newMesh);
        setMesh(newMesh);
        console.log("Added new mesh to scene");

        // Fit camera for clear height/length view (side view: length X base, height Y up)
        const scaledMaxDim = 3.5; // After uniform scaling
        cameraRef.current.position.set(scaledMaxDim * 1.5, scaledMaxDim / 1, scaledMaxDim * 2);
        controlsRef.current.target.copy(new THREE.Vector3(0, 0, 0)); // Center after positioning
        controlsRef.current.update();

        rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    const handleUpdate = async () => {
        setLoading(true);
        setMessage(null);
        setActiveStep(0);

        // Clear old mesh immediately on button click
        clearSceneMeshes();

        try {
            // Step 1: Update Variables
            setActiveStep(1);
            setMessage({ type: "info", text: "Updating cube dimensions..." });

            await apiClient.post("/api/onshape/update-variables", dimensions);

            setMessage({
                type: "success",
                text: "Cube dimensions updated successfully! Loading 3D preview...",
            });

            // Step 2: Download STL and Load Preview
            setActiveStep(2);
            const response = await apiClient.get("/api/onshape/download-stl", {
                responseType: "blob",
            });

            const stlBlob = response.data;
            await loadSTL(stlBlob);

            setMessage({
                type: "success",
                text: "3D model preview loaded! You can now download PDF or STL.",
            });

            setIsUpdated(true);
            setActiveStep(0);
        } catch (error) {
            console.error("Error:", error);
            setMessage({
                type: "error",
                text: error.response?.data?.message || "Failed to update or load 3D preview",
            });
            setActiveStep(0);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadSTL = async () => {
        try {
            const response = await apiClient.get("/api/onshape/download-stl", {
                responseType: "blob",
            });

            const blob = new Blob([response.data], { type: "application/octet-stream" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `cube_model_${dimensions.length}x${dimensions.width}x${dimensions.height}.stl`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setMessage({
                type: "success",
                text: "STL downloaded successfully!",
            });
        } catch (error) {
            setMessage({
                type: "error",
                text: "Failed to download STL",
            });
        }
    };

    // --- REVISED PDF DOWNLOAD HANDLER TO INCLUDE DRAWING CREATION ---
    const handleDownloadPDF = async () => {
        setLoading(true);
        setMessage(null);
        setActiveStep(0);

        try {
            // Step 1: Create the new Drawing Tab (Active Step 3)
            setActiveStep(3);
            setMessage({ type: "info", text: "Creating new drawing tab from template..." });

            const createResponse = await apiClient.post("/api/onshape/create-drawing");
            const drawingElementId = createResponse.data.drawingElementId;

            setMessage({ type: "success", text: "Drawing tab created! Initiating PDF export..." });

            // Step 2: Export drawing to PDF (Active Step 4)
            setActiveStep(4);
            setMessage({ type: "info", text: "Initiating PDF export..." });

            // Send the new Drawing Element ID to the export endpoint
            const exportResponse = await apiClient.post("/api/onshape/export-drawing", {
                drawingElementId: drawingElementId,
            });
            const translationId = exportResponse.data.translationId;

            // Step 3: Poll for completion and download
            setMessage({ type: "info", text: "Processing PDF... Please wait." });

            const pdfBlob = await pollAndDownload(translationId);

            // Step 4: Download PDF
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `cube_drawing_${dimensions.length}x${dimensions.width}x${dimensions.height}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setMessage({ type: "success", text: "PDF downloaded successfully!" });
            setActiveStep(0);
        } catch (error) {
            console.error("PDF Download Error:", error);
            setMessage({
                type: "error",
                text: error.response?.data?.message || "Failed to create or generate PDF drawing.",
            });
            setActiveStep(0);
        } finally {
            setLoading(false);
        }
    };

    const pollAndDownload = async (translationId) => {
        const maxAttempts = 30;
        const pollInterval = 2000; // 2 seconds

        for (let i = 0; i < maxAttempts; i++) {
            const statusResponse = await apiClient.get(`/api/onshape/translation-status/${translationId}`);

            if (statusResponse.data.requestState === "DONE") {
                // Download the PDF
                const pdfResponse = await apiClient.get("/api/onshape/download-pdf", {
                    params: {
                        documentId: statusResponse.data.resultDocumentId,
                        workspaceId: statusResponse.data.resultWorkspaceId,
                        elementId: statusResponse.data.resultElementIds[0],
                    },
                    responseType: "blob",
                });

                return pdfResponse.data;
            } else if (statusResponse.data.requestState === "FAILED") {
                throw new Error("PDF generation failed: " + statusResponse.data.failureReason);
            }

            // Wait before next poll
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        throw new Error("PDF generation timeout");
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, p: 3 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    Cube Configurator
                </Typography>

                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                    Configure your cube dimensions, preview the 3D model, and download the technical drawing
                </Typography>

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Grid container spacing={3}>
                    {/* Controls Panel */}
                    <Grid item xs={12} md={4}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Height (cm)"
                                    type="number"
                                    value={dimensions.height}
                                    onChange={handleChange("height")}
                                    inputProps={{ min: 1, step: 0.1 }}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Length (cm)"
                                    type="number"
                                    value={dimensions.length}
                                    onChange={handleChange("length")}
                                    inputProps={{ min: 1, step: 0.1 }}
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Width (cm)"
                                    type="number"
                                    value={dimensions.width}
                                    onChange={handleChange("width")}
                                    inputProps={{ min: 1, step: 0.1 }}
                                    disabled={loading}
                                />
                            </Grid>
                        </Grid>

                        <Stack spacing={2} sx={{ mt: 3 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleUpdate}
                                disabled={loading}
                                startIcon={
                                    loading && activeStep === 1 ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <UpdateIcon />
                                    )
                                }
                                color="primary"
                            >
                                {loading && activeStep === 1 ? "Updating..." : "Update & Preview 3D"}
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                size="large"
                                onClick={handleDownloadSTL}
                                disabled={loading || !isUpdated}
                                startIcon={<DownloadIcon />}
                                color="info"
                            >
                                Download STL
                            </Button>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleDownloadPDF}
                                disabled={loading || !isUpdated}
                                startIcon={
                                    loading && activeStep >= 3 ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <DownloadIcon />
                                    )
                                }
                                color="success"
                            >
                                {loading && activeStep >= 3 ? "Processing..." : "Download PDF"}
                            </Button>
                        </Stack>

                        {!isUpdated && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                align="center"
                                sx={{ mt: 2, display: "block" }}
                            >
                                Update dimensions to preview and download
                            </Typography>
                        )}
                    </Grid>

                    {/* 3D Preview */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={2} sx={{ height: 400, p: 1 }}>
                            <Box ref={mountRef} sx={{ width: "100%", height: "100%" }} />
                            {!isUpdated && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        textAlign: "center",
                                        color: "text.secondary",
                                    }}
                                >
                                    <Typography variant="h6">3D Preview</Typography>
                                    <Typography variant="body2">Loading initial model...</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {message && (
                    <Alert severity={message.type} sx={{ mt: 2 }}>
                        {message.text}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default CubeConfigurator;
